import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { codesService } from '../services/codes';

// Async thunks
export const fetchAllCodes = createAsyncThunk(
  'codes/fetchAllCodes',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await codesService.getAllCodes(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch codes');
    }
  }
);

export const fetchCodes = createAsyncThunk(
  'codes/fetchCodes',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await codesService.getCodes(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch codes');
    }
  }
);

export const createCode = createAsyncThunk(
  'codes/createCode',
  async (codeData, { rejectWithValue }) => {
    try {
      const response = await codesService.createCode(codeData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create code');
    }
  }
);

export const updateCode = createAsyncThunk(
  'codes/updateCode',
  async ({ id, codeData }, { rejectWithValue }) => {
    try {
      const response = await codesService.updateCode(id, codeData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update code');
    }
  }
);

export const deleteCode = createAsyncThunk(
  'codes/deleteCode',
  async (id, { rejectWithValue }) => {
    try {
      await codesService.deleteCode(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete code');
    }
  }
);

const initialState = {
  codes: [],
  model: null,
  pagination: null,
  loading: false,
  error: null,
  searchTerm: '',
  filters: {
    power: '',
    mode: '',
    temperature: '',
    fan_speed: '',
    swing: '',
  },
};

const codesSlice = createSlice({
  name: 'codes',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
      state.searchTerm = '';
    },
    clearCodes: (state) => {
      state.codes = [];
      state.model = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all codes (for dashboard)
      .addCase(fetchAllCodes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllCodes.fulfilled, (state, action) => {
        state.loading = false;
        state.codes = action.payload.ir_codes;
        state.pagination = action.payload.pagination;
        state.model = null; // Clear model when fetching all codes
        state.error = null;
      })
      .addCase(fetchAllCodes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch codes
      .addCase(fetchCodes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCodes.fulfilled, (state, action) => {
        state.loading = false;
        state.codes = action.payload.ir_codes;
        state.pagination = action.payload.pagination;
        state.model = action.payload.model;
        state.error = null;
      })
      .addCase(fetchCodes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create code
      .addCase(createCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCode.fulfilled, (state, action) => {
        state.loading = false;
        state.codes.unshift(action.payload.ir_code);
        state.error = null;
      })
      .addCase(createCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update code
      .addCase(updateCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCode.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.codes.findIndex(c => c.id === action.payload.ir_code.id);
        if (index !== -1) {
          state.codes[index] = action.payload.ir_code;
        }
        state.error = null;
      })
      .addCase(updateCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete code
      .addCase(deleteCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCode.fulfilled, (state, action) => {
        state.loading = false;
        state.codes = state.codes.filter(c => c.id !== action.payload);
        state.error = null;
      })
      .addCase(deleteCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setSearchTerm, setFilters, clearFilters, clearCodes } = codesSlice.actions;
export default codesSlice.reducer;
